import { useMemo, useState, useEffect } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, KeyRound, Lock, Mail, Phone, Sparkles, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { PASSWORD_MIN_LENGTH } from "@/lib/authRules";

/** Version stamp persisted to user_consents.version. Bump on legal doc changes. */
const SIGNUP_TERMS_VERSION = "2026-05-13";

async function getFunctionErrorMessage(error: any) {
  const fallback = error?.message ?? "Unexpected signup error";
  const context = error?.context;
  if (!context || typeof context.json !== "function") return fallback;
  try {
    const body = await context.json();
    return body?.error ?? body?.message ?? fallback;
  } catch {
    return fallback;
  }
}

export default function AuthPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const initialAuthMode = useMemo(() => searchParams.get("intent")?.toLowerCase() ?? "", [searchParams]);
  const [isLogin, setIsLogin] = useState(!["signup", "register"].includes(initialAuthMode));
  const initialEmail = useMemo(() => searchParams.get("email")?.trim() ?? "", [searchParams]);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const initialCompanyName = useMemo(() => searchParams.get("company_name")?.trim() ?? "", [searchParams]);
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const initialInviteToken = useMemo(() => {
    return (
      searchParams.get("invite")?.trim()
      || searchParams.get("company_invite_token")?.trim()
      || searchParams.get("invite_token")?.trim()
      || searchParams.get("token")?.trim()
      || ""
    );
  }, [searchParams]);
  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState("");
  const [pendingConfirmationLinkEmail, setPendingConfirmationLinkEmail] = useState("");
  const [verificationSendFailed, setVerificationSendFailed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // countdown for resend button (client-side UX); server enforces stronger limits
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);
  const [resetSending, setResetSending] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  useEffect(() => {
    if (resetCooldown <= 0) return;
    const id = setInterval(() => setResetCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);
  const { toast } = useToast();
  const selectedPackageId = searchParams.get("package");
  const selectedPlan = searchParams.get("plan")?.trim().toLowerCase() ?? "";
  const { data: selectedPlanPackage, isLoading: selectedPlanLoading } = useQuery({
    queryKey: ["auth-selected-plan-package", selectedPlan],
    queryFn: async () => {
      const candidates = Array.from(new Set([
        selectedPlan,
        `${selectedPlan}-v2`,
        selectedPlan === "starter" ? "news-site-starter" : "",
        selectedPlan === "growth" ? "growth-v2" : "",
        selectedPlan === "network" ? "network-v2" : "",
        selectedPlan === "agency" ? "agency-v2" : "",
      ].filter(Boolean)));
      const { data, error } = await supabase
        .from("packages")
        .select("id, slug")
        .in("slug", candidates)
        .eq("is_active", true)
        .limit(10);
      if (error) throw error;
      return data?.find((pkg) => pkg.slug === selectedPlan)
        ?? data?.find((pkg) => pkg.slug === `${selectedPlan}-v2`)
        ?? data?.[0]
        ?? null;
    },
    enabled: !!user && !!selectedPlan && !selectedPackageId,
  });

  if (loading) return null;
  if (user) {
    if (selectedPackageId) return <Navigate to={`/checkout/package/${selectedPackageId}`} replace />;
    if (selectedPlan) {
      if (selectedPlanLoading) return null;
      return <Navigate to={selectedPlanPackage?.id ? `/checkout/package/${selectedPlanPackage.id}` : "/pricing"} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleForgotPassword = async () => {
    const target = email.trim();
    if (!target) {
      toast({
        title: t("auth.resetEmailRequiredToast"),
        description: t("auth.resetEmailRequiredDescription"),
        variant: "destructive",
      });
      return;
    }
    if (resetSending || resetCooldown > 0) return;

    setResetSending(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      // Neutral confirmation: never reveal whether the address has an account.
      setResetCooldown(60);
      toast({
        title: t("auth.resetSentToast"),
        description: t("auth.resetSentDescription", { email: target }),
      });
    } catch (err: any) {
      toast({
        title: t("auth.authErrorToast"),
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    } finally {
      setResetSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast({ title: t("auth.welcomeBackToast") });
      } else {
        if (!termsAccepted) {
          toast({
            title: t("auth.actionRequiredToast"),
            description: t("auth.termsRequiredDescription"),
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        console.info("Delapresse signup request start", { email, hasCompanyName: Boolean(companyName.trim()), hasInviteToken: Boolean(inviteToken.trim()) });
        const { data: signupData, error } = await supabase.functions.invoke("signup-account", {
          body: {
            email,
            password,
            full_name: fullName.trim(),
            phone_number: phoneNumber.trim(),
            company_name: companyName.trim(),
            company_invite_token: inviteToken.trim(),
            terms_accepted: termsAccepted,
            terms_version: SIGNUP_TERMS_VERSION,
            user_agent: navigator.userAgent.slice(0, 500),
            redirect_to: window.location.origin,
          },
        });
        console.info("Delapresse signup function response", { data: signupData, error });
        if (error) throw new Error(await getFunctionErrorMessage(error));
        setVerificationSendFailed(false);
        setPendingVerificationEmail("");
        setPendingConfirmationLinkEmail(email);
        console.info("Delapresse signup final UI status", { email, status: "verification_email_sent" });
        toast({
          title: t("auth.accountCreatedToast"),
          description: t("auth.accountCreatedDescription"),
        });
      }
    } catch (err: any) {
      const message = err?.message ?? String(err);
      console.error("Delapresse signup final UI status", { email, status: "failed", error: message });
      toast({
        title: t("auth.authErrorToast"),
        description: message.includes("already exists")
          ? t("auth.accountExistsDescription")
          : message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("verify-email-code", {
        body: { email: pendingVerificationEmail, code: verificationCode },
      });
      if (error) throw error;
      toast({ title: t("auth.trialActivatedToast"), description: t("auth.trialActivatedDescription") });
      setIsLogin(true);
      setPendingVerificationEmail("");
      setVerificationCode("");
    } catch (err: any) {
      toast({ title: t("auth.verifyFailedToast"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Delapresse</h1>
          <p className="text-sm text-muted-foreground">
            {isLogin ? t("auth.loginHeading") : t("auth.signupHeading")}
          </p>
        </div>

        {pendingConfirmationLinkEmail ? (
          <div className="space-y-4 rounded-xl border border-border bg-card p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  {t(verificationSendFailed ? "auth.confirmationLinkErrorHeading" : "auth.confirmationLinkHeading")}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {verificationSendFailed
                    ? t("auth.confirmationLinkErrorBody", { email: pendingConfirmationLinkEmail })
                    : t("auth.accountCreatedDescription")}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={async () => {
                  const email = pendingConfirmationLinkEmail;
                  if (!email || resendCooldown > 0) return;
                  setResendCooldown(60);
                  try {
                    const { error, data } = await supabase.functions.invoke("send-verification-code", { body: { email } });
                    if (error) throw error;
                    if ((data as any)?.status === "sent") {
                      setPendingVerificationEmail(email);
                      setPendingConfirmationLinkEmail("");
                      toast({ title: t("auth.trialCreatedToast"), description: t("auth.trialCreatedDescription") });
                    } else {
                      toast({ title: t("auth.confirmationLinkToast"), description: t("auth.confirmationLinkDescription") });
                    }
                  } catch (err: any) {
                    toast({ title: t("auth.authErrorToast"), description: err?.message ?? String(err), variant: "destructive" });
                  }
                }}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? t("auth.resendCooldown", { seconds: resendCooldown }) : t("auth.resendButton", "Resend verification email")}
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setPendingConfirmationLinkEmail("");
                  setIsLogin(true);
                }}
              >
                {t("auth.backToLogin", "Back to Sign In")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : pendingVerificationEmail ? (
          <form onSubmit={verifyCode} className="space-y-4 rounded-xl border border-border bg-card p-6">
            <div className="space-y-2">
              <Label htmlFor="verification-code" className="text-foreground">{t("auth.verificationCodeLabel")}</Label>
              <Input
                id="verification-code"
                inputMode="numeric"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                required
              />
              <p className="text-xs text-muted-foreground">{t("auth.verificationCodeSent", { email: pendingVerificationEmail })}</p>
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t("auth.verifySubmitting") : t("auth.verifySubmit")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <div className="pt-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={async () => {
                  if (resendCooldown > 0) return;
                  const email = pendingVerificationEmail;
                  if (!email) return;
                  setResendCooldown(60);
                  try {
                    const { error, data } = await supabase.functions.invoke("send-verification-code", { body: { email } });
                    if (error) throw error;
                    if ((data as any)?.status === "sent") {
                      toast({ title: t("auth.trialCreatedToast"), description: t("auth.verificationCodeSent", { email }) });
                    } else {
                      toast({ title: t("auth.confirmationLinkToast"), description: t("auth.confirmationLinkDescription") });
                    }
                  } catch (err: any) {
                    toast({ title: t("auth.verifyFailedToast"), description: err?.message ?? String(err), variant: "destructive" });
                  }
                }}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? t("auth.resendCooldown", { seconds: resendCooldown }) : t("auth.resendButton", "Resend verification email")}
              </Button>
            </div>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-6">
          {!isLogin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground">{t("auth.fullNameLabel")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t("auth.fullNamePlaceholder")} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground">
                  {t("auth.phoneLabel")} <span className="text-muted-foreground font-normal">{t("auth.phoneOptional")}</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+33 6 12 34 56 78"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("auth.phoneHelper")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-foreground">
                  {t("auth.companyLabel")} {inviteToken ? <span className="text-muted-foreground font-normal">{t("auth.phoneOptional")}</span> : null}
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("auth.companyPlaceholder")} className="pl-10" required={!inviteToken} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("auth.companyHelper")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite" className="text-foreground">
                  {t("auth.inviteLabel")} <span className="text-muted-foreground font-normal">{t("auth.phoneOptional")}</span>
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="invite" value={inviteToken} onChange={(e) => setInviteToken(e.target.value)} placeholder={t("auth.invitePlaceholder")} className="pl-10" />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("auth.inviteHelper")}
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">{t("auth.emailLabel")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} className="pl-10" required />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="password" className="text-foreground">{t("auth.passwordLabel")}</Label>
              {isLogin && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetSending || resetCooldown > 0}
                  className="text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                >
                  {resetCooldown > 0
                    ? t("auth.resendCooldown", { seconds: resetCooldown })
                    : resetSending
                      ? t("auth.forgotPasswordSending")
                      : t("auth.forgotPassword")}
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.passwordPlaceholder")} className="pl-10" minLength={PASSWORD_MIN_LENGTH} required />
            </div>
          </div>

          {!isLogin && (
            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="signup-terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
              />
              <Label htmlFor="signup-terms" className="text-xs leading-5 text-muted-foreground">
                {t("auth.termsPrefix")}{" "}
                <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t("auth.termsCgv")}</Link>{" "}
                {t("auth.termsAnd")}{" "}
                <Link to="/politique-rgpd" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t("auth.termsPrivacy")}</Link>.
              </Label>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting || (!isLogin && !termsAccepted)}>
            {submitting
              ? t("auth.submitLoading")
              : isLogin
                ? t("auth.submitLogin")
                : t("auth.submitSignup")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
        )}

        {!pendingVerificationEmail && <p className="text-center text-sm text-muted-foreground">
          {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
          <button onClick={() => setIsLogin(!isLogin)} className="font-medium text-primary hover:underline">
            {isLogin ? t("auth.switchToRegister") : t("auth.switchToLogin")}
          </button>
        </p>}
      </div>
    </div>
  );
}
